import React from 'react';
import {Chip, Stack, Typography} from '@mui/material';
import {formatLastSeen} from '../../utils/formatters';
import {ROLES} from '../../config/roles';
import apiService from '../../services/apiService';
import EditableCard from '../EditableCard';
import MachineBasicForm from '../forms/MachineBasicForm';

/**
 * "Details" card on the machine detail page. Read shows every Machine
 * identity + operational field; edit covers only the operator-editable
 * subset (name + disabled), persisted via PUT /api/machines/{id}/basic.
 *
 * Auth declared on this component; EditableCard's <Protected> wrappers
 * consume the .abilities map.
 */
const MachineDetailsCard = ({machine, onUpdated}) => (
    <EditableCard
        title="Details"
        protectedBy={MachineDetailsCard}
        protectAction="edit"
        value={{name: machine.name, disabled: !!machine.disabled}}
        save={(draft) => apiService.put(`/api/machines/${machine.id}/basic`, draft)}
        onSaved={onUpdated}
        successMessage="Machine details saved"
        renderView={() => <DetailsView machine={machine}/>}
        renderForm={(draft, setDraft) => (
            <MachineBasicForm value={draft} onChange={setDraft}/>
        )}
    />
);

const DetailsView = ({machine}) => (
    <Stack spacing={1}>
        <Field label="Name" value={machine.name}/>
        <Field label="ID" value={machine.id}/>
        <Field label="MAC" value={machine.mac}/>
        <Field label="Internal ID" value={machine.internalId}/>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2"><strong>State:</strong></Typography>
            <Chip label={machine.state}
                  color={machine.state === 'online' ? 'success' : 'error'}
                  size="small"/>
        </Stack>
        <Field label="Last Seen" value={formatLastSeen(machine.lastHeartbeatTime)}/>
        <Field label="Disabled" value={machine.disabled ? 'Yes' : 'No'}/>
    </Stack>
);

const Field = ({label, value}) => (
    <Typography variant="body2" sx={{wordBreak: 'break-all'}}>
        <strong>{label}:</strong> {value ?? '—'}
    </Typography>
);

MachineDetailsCard.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    edit: [ROLES.ADMINS],
};

export default MachineDetailsCard;
